/**
 * The Live Wall's Recorded mode: its shape, and how a live wall hands its cameras over.
 *
 * The shape is a rows × columns grid (1–4 each) that the viewer sets, optionally with the
 * top-left 2×2 played as one big window. It opens as "1 + 5" — 3×3 with the big window. Column
 * widths and row heights are the viewer's too (`colSizes` / `rowSizes`, CSS `fr` weights).
 *
 * Whatever the shape, it holds at most WALL_PLAYBACK_MAX windows, which is also the cap on
 * cameras played at once: every window is an HLS stream the node may have to transcode on
 * demand, and a 16-tile playback wall would queue most of its tiles behind the playback
 * service's transcode slots.
 *
 * The hand-over travels in the URL (`?cameras=a,b,c&view=…`), not router state, so a reload or a
 * shared link lands on the same cameras, and Recorded → Live returns to the layout it came from.
 */
import { buildWindows, flowCameras, type WallMerge } from '../live-wall/wallLayout';

export const WALL_PLAYBACK_MAX = 9;
export const WALL_PLAYBACK_MAX_ROWS = 4;
export const WALL_PLAYBACK_MAX_COLS = 4;

export interface WallPlaybackLayout {
  rows: number;
  cols: number;
  /** Top-left 2×2 as one window — applied only where the grid can take it (see hasBigWindow). */
  big: boolean;
  /** One `fr` weight per column / row. */
  colSizes: number[];
  rowSizes: number[];
}

export const DEFAULT_WALL_PLAYBACK_LAYOUT: WallPlaybackLayout = {
  rows: 3, cols: 3, big: true, colSizes: [1, 1, 1], rowSizes: [1, 1, 1],
};

/** A big window needs a 2×2 to occupy and something left beside it. */
export const canHaveBigWindow = (rows: number, cols: number) => rows >= 2 && cols >= 2 && rows * cols > 4;
export const hasBigWindow = (l: Pick<WallPlaybackLayout, 'rows' | 'cols' | 'big'>) => l.big && canHaveBigWindow(l.rows, l.cols);
export const windowCountOf = (l: Pick<WallPlaybackLayout, 'rows' | 'cols' | 'big'>) =>
  l.rows * l.cols - (hasBigWindow(l) ? 3 : 0);

const gridOf = (l: WallPlaybackLayout) => `${l.rows}x${l.cols}`;
const mergesOf = (l: WallPlaybackLayout): WallMerge[] => (hasBigWindow(l) ? [{ position: 0, colSpan: 2, rowSpan: 2 }] : []);

const clampInt = (n: unknown, lo: number, hi: number, dflt: number) => {
  const v = Math.round(Number(n));
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : dflt;
};
/** `n` weights, keeping the ones given (sane values only) and padding with their mean. */
function fitSizes(sizes: unknown, n: number): number[] {
  const given = (Array.isArray(sizes) ? sizes : []).map(Number).filter((x) => Number.isFinite(x) && x > 0.05 && x < 20).slice(0, n);
  const mean = given.length ? given.reduce((a, b) => a + b, 0) / given.length : 1;
  while (given.length < n) given.push(mean);
  return given;
}

/**
 * A layout from anywhere (storage, an edit) made drawable: dims clamped, sizes fitted to them,
 * and never more than WALL_PLAYBACK_MAX windows — the big window is switched on, then rows and
 * columns trimmed, until it fits.
 */
export function normalizeWallPlaybackLayout(raw: Partial<WallPlaybackLayout> | null | undefined): WallPlaybackLayout {
  const d = DEFAULT_WALL_PLAYBACK_LAYOUT;
  let rows = clampInt(raw?.rows, 1, WALL_PLAYBACK_MAX_ROWS, d.rows);
  let cols = clampInt(raw?.cols, 1, WALL_PLAYBACK_MAX_COLS, d.cols);
  let big = typeof raw?.big === 'boolean' ? raw.big : d.big;
  while (windowCountOf({ rows, cols, big }) > WALL_PLAYBACK_MAX) {
    if (!big && canHaveBigWindow(rows, cols)) big = true;
    else if (cols >= rows) cols--;
    else rows--;
  }
  return { rows, cols, big, colSizes: fitSizes(raw?.colSizes, cols), rowSizes: fitSizes(raw?.rowSizes, rows) };
}

/** The shape after an edit, or null when it would hold more than WALL_PLAYBACK_MAX windows. */
export function editWallPlaybackLayout(
  l: WallPlaybackLayout,
  edit: Partial<Pick<WallPlaybackLayout, 'rows' | 'cols' | 'big'>>,
): WallPlaybackLayout | null {
  const rows = edit.rows ?? l.rows;
  const cols = edit.cols ?? l.cols;
  const big = edit.big ?? l.big;
  if (rows < 1 || cols < 1 || rows > WALL_PLAYBACK_MAX_ROWS || cols > WALL_PLAYBACK_MAX_COLS) return null;
  if (windowCountOf({ rows, cols, big }) > WALL_PLAYBACK_MAX) return null;
  return { rows, cols, big, colSizes: fitSizes(l.colSizes, cols), rowSizes: fitSizes(l.rowSizes, rows) };
}

export interface WallPlaybackSlot {
  cameraId: string;
  cameraName: string;
  gridPosition: number;
}

/** The layout's windows, big first then reading order — the order cameras are poured in. */
export const wallPlaybackWindows = (l: WallPlaybackLayout = DEFAULT_WALL_PLAYBACK_LAYOUT) =>
  [...buildWindows(gridOf(l), mergesOf(l))]
    .sort((a, b) => b.rowSpan * b.colSpan - a.rowSpan * a.colSpan || a.position - b.position);

/**
 * A live wall's cameras in the order they should arrive: its windows largest first, then
 * reading order — so the camera in a live "1 + 7"'s big window lands in the big window here too.
 * Anything past the sixth is left behind, which is the stated rule.
 */
export function liveWallHandOver<T extends { cameraId: string }>(
  gridCameras: (T | null)[],
  gridSize: string,
  merges: WallMerge[],
): string[] {
  const order = [...buildWindows(gridSize, merges)]
    .sort((a, b) => b.rowSpan * b.colSpan - a.rowSpan * a.colSpan || a.position - b.position);
  const ids: string[] = [];
  for (const w of order) {
    const cam = gridCameras[w.position];
    if (cam && !ids.includes(cam.cameraId)) ids.push(cam.cameraId);
    if (ids.length === WALL_PLAYBACK_MAX) break;
  }
  return ids;
}

/** Where the Live | Recorded toggle goes from a live wall. */
export function wallPlaybackHref(playbackPath: string, cameraIds: string[], viewId?: string | null): string {
  const qs = new URLSearchParams();
  if (cameraIds.length) qs.set('cameras', cameraIds.slice(0, WALL_PLAYBACK_MAX).join(','));
  if (viewId) qs.set('view', viewId);
  const s = qs.toString();
  return s ? `${playbackPath}?${s}` : playbackPath;
}

/** Where the toggle goes back to: the live wall, on the layout Recorded mode was opened from. */
export function liveWallHref(livePath: string, viewId?: string | null): string {
  return viewId ? `${livePath}?view=${encodeURIComponent(viewId)}` : livePath;
}

export function parseCameraIds(raw: string | null): string[] {
  const out: string[] = [];
  for (const id of (raw || '').split(',').map((s) => s.trim()).filter(Boolean)) {
    if (!out.includes(id)) out.push(id);
    if (out.length === WALL_PLAYBACK_MAX) break;
  }
  return out;
}

/** Ids → a rows×cols cell grid with the cameras in the layout's windows, big first. Extras drop. */
export function slotsFromIds(
  ids: string[], nameOf: (id: string) => string, l: WallPlaybackLayout = DEFAULT_WALL_PLAYBACK_LAYOUT,
): (WallPlaybackSlot | null)[] {
  const flat = ids.map((cameraId, i) => ({ cameraId, cameraName: nameOf(cameraId), gridPosition: i }));
  return flowCameras(flat, gridOf(l), mergesOf(l));
}

/** The inverse, for keeping `?cameras=` in step with the wall: big window first. */
export function idsFromSlots(slots: (WallPlaybackSlot | null)[], l: WallPlaybackLayout = DEFAULT_WALL_PLAYBACK_LAYOUT): string[] {
  return wallPlaybackWindows(l).map((w) => slots[w.position]?.cameraId).filter(Boolean) as string[];
}
